import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Button,
    Pagination,
    Box,
    CircularProgress,
    Alert,
    TextareaAutosize,
    createTheme,
    ThemeProvider,
    Grid,
} from '@mui/material';
import { createTerm, getTerms } from '../../../API/ConfigSettings/ConfigSettingsAPI';
import { styled } from '@mui/material/styles';

const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
    },
});

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2), 
    marginBottom: theme.spacing(2),
}));

function TermsAndCond() {
    const [terms, setTerms] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newTerm, setNewTerm] = useState({
        body: '',
    });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    const itemsPerPage = 6;

    useEffect(() => {
        const fetchTerms = async () => {
            setLoading(true);
            setError('');

            try {
                const data = await getTerms(currentPage, itemsPerPage);
                setTerms(data.terms);
                setTotalPages(data.totalPages);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching terms:", err);
                setError(err.message || 'Failed to load terms and conditions.');
                setLoading(false);
            }
        };

        fetchTerms();
    }, [currentPage, itemsPerPage]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleInputChange = (e) => {
        setNewTerm({ ...newTerm, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setCreating(true);
        setCreateError('');

        try {
            const createdTerm = await createTerm({
                body: newTerm.body,
            });

            setTerms([createdTerm, ...terms]);
            setNewTerm({ body: '' });
            setCreating(false);

            const data = await getTerms(currentPage, itemsPerPage);
            setTerms(data.terms);
            setTotalPages(data.totalPages);
            setLoading(false);

        } catch (err) {
            console.error("Error creating term:", err.response ? err.response.data : err.message);
            setCreateError(err.response?.data?.message || err.message || 'Failed to create term.');
            setCreating(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container > 
                <Typography variant="h4" component="h2" gutterBottom textAlign="center">
                    Terms and Conditions
                </Typography>

                <Grid container spacing={3} justifyContent="center"> 
      
                    <Grid item xs={6} md={0}> 
                        <StyledPaper >
                            <Typography variant="h6" component="h3" gutterBottom>
                                Create New Term
                            </Typography>
                            <form onSubmit={handleSubmit}>
                                <TextareaAutosize
                                    placeholder="Body"
                                    name="body"
                                    value={newTerm.body}
                                    onChange={handleInputChange}
                                    style={{ width: '100%', minHeight: '100px', padding: '8px', borderColor: '#ccc', borderRadius: '4px' }}
                                    required
                                />
                                {createError && <Alert severity="error">{createError}</Alert>}
                                <Button variant="contained" color="primary" type="submit" disabled={creating}>
                                    {creating ? <CircularProgress size={24} color="inherit" /> : 'Create'}
                                </Button>
                            </form>
                        </StyledPaper>
                    </Grid>

    
                    <Grid item xs={6} md={0}> 
                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Alert severity="error">{error}</Alert>
                        ) : (
                            <>
                                {terms.length > 0 ? (
                                    terms.map((term) => (
                                        <StyledPaper key={term._id} elevation={3}>
                                            <Typography variant="h6" component="h3" gutterBottom>
                                                Version: {term.version}
                                            </Typography>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Published: {new Date(term.publishDate).toLocaleDateString()}
                                            </Typography>
                                            <Typography variant="body1" dangerouslySetInnerHTML={{ __html: term.body }} />
                                        </StyledPaper>
                                    ))
                                ) : (
                                    <Alert severity="info">No terms and conditions found.</Alert>
                                )}

                                <Box display="flex" justifyContent="center" mt={3}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        showFirstButton
                                        showLastButton
                                    />
                                </Box>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Container>
        </ThemeProvider>
    );
}

export default TermsAndCond;